import React, { useState, useEffect } from 'react'

import { withRouter, RouteComponentProps } from 'react-router-dom'

import { Header, Segment } from 'semantic-ui-react'
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Card,
  CardContent,
  CardMedia,
  Link,
} from '@mui/material'

import ReactMapboxGl, { ZoomControl, Layer, Feature } from 'react-mapbox-gl'

import { MAPBOX_API_KEY } from '../../../config/environments'
import { locales } from '../../../config/constants'
import { getBbox } from '../../../utils/sharedFunctions'
import { formatNumber } from '../../../utils/sharedFunctions'
import InviteCard from './InviteCard'
import BrandingCard from './BrandingCard'
import FormatsCard from './FormatsCard'
import SettingsCard from './SettingsCard'
import { IClip, IProject } from '../../../api/Data'
import LegalComplianceCard from './LegalComplianceCard'

import ExternalLink from '../../../components/common/ExternalLink'

import EmptyShelves from '../../../_images/empty-shelves.png'

import moment from 'moment'

const MAX_NUMBER_OF_QUESTIONS = 8

const MAX_QUESTIONS_CHARACTER_LENGTH = 260

const styles = {
  paper: {
    padding: '16px',
  },
  statBox: {
    textAlign: 'center',
  },
}

const Map = MAPBOX_API_KEY
  ? ReactMapboxGl({
      accessToken: MAPBOX_API_KEY,
      touchZoomRotate: true,
    })
  : null

const ConfigureTabOverviewPage = (props: IProps) => {
  const { history, project, clips, handleChange, handleChangePrimaryFont, handleChangePrimaryColor, validationError } =
    props

  const [projectTitle, setProjectTitle] = useState(project.title)
  const [projectName, setProjectName] = useState(project.name)
  const [projectReward, setProjectReward] = useState(project.incentive)
  const [projectLocale, setProjectLocale] = useState(project.locale)
  const [numberOfQuestions, setNumberOfQuestions] = useState(project.numberOfQuestions || 1)
  const [questions, setQuestions] = useState<string[]>(project.questions || [])
  const [isShared, setIsShared] = useState(project.shared)

  const projectLinkForQRCode = `${project.projectUrl}?utm_source=qrcode` // utm is used to differentiate direct form qrcode scan

  useEffect(() => {
    setProjectTitle(project.title)
    setProjectName(project.name)
    setProjectReward(project.incentive)
    setProjectLocale(project.locale)
    setNumberOfQuestions(project.numberOfQuestions || 1)
    setQuestions(project.questions || [])
  }, [project])

  const handleProjectTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.title = e.target.value

    setProjectTitle(e.target.value)

    handleChange(e, { name: 'title', value: e.target.value })
  }

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.name = e.target.value

    setProjectName(e.target.value)

    handleChange(e, { name: 'name', value: e.target.value })
  }

  const handleProjectRewardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.incentive = e.target.value

    setProjectReward(e.target.value)

    handleChange(e, { name: 'reward', value: e.target.value })
  }

  const handleProjectLocaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.locale = e.target.value

    setProjectLocale(e.target.value)

    handleChange(e, { name: 'locale', value: e.target.value })
  }

  const handleProjectLiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.shared = e.target.checked

    setIsShared(e.target.checked)

    handleChange(e, { name: 'shared', value: e.target.checked })
  }

  const handleDropdownChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: any) => {
    setNumberOfQuestions(data.value)

    handleChange(e, { name: 'numberOfQuestions', value: data.value })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const questionNumber = parseInt(name.split('-')[1], 10) - 1
    const updatedQuestions = [...questions]

    updatedQuestions[questionNumber] = value
    setQuestions(updatedQuestions)

    handleChange(e, { name: 'questions', values: updatedQuestions })
  }

  const dropdownOptions = Array.from({ length: MAX_NUMBER_OF_QUESTIONS }, (_, i) => ({
    key: i + 1,
    text: i + 1,
    value: i + 1,
  }))

  const getLatestEntries = () => {
    return clips.slice(0, 6)
  }

  const goToClipGalleryPage = () => {
    history.push(`/projects/${project.slug}/curate`)
  }

  const goToClipPage = (clip: IClip) => {
    history.push(`/projects/${project.slug}/curate?clipId=${clip.id}`)
  }

  const getClipsWithLocation = () => {
    return clips.filter((clip) => clip.lat1 && clip.lon1)
  }

  const bbox: any = getBbox(clips)

  const latestEntries: IClip[] = getLatestEntries()

  const clipsWithLocation: IClip[] = getClipsWithLocation()

  return (
    <>
      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Overview & Settings
        <Header.Subheader>
          Get a bird’s-eye view of your project at a glance. Track submission progress, view stats, and jump into action
          from one dashboard.
          <br />
          It’s here you can also configure your project settings.
        </Header.Subheader>
      </Header>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={styles.paper}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="h5" fontWeight="bold">
                    Latest Video Submissions
                  </Typography>
                  {latestEntries.length === 0 ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <img src={EmptyShelves} width="100%" />
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Typography variant="h6" fontWeight="bold">
                          Empty Shelves
                        </Typography>
                        <p>
                          Your project video library is looking a bit bare. Share the link with contributors or upload a
                          test video yourself to bring it to life.
                        </p>
                        <Typography variant="body2">
                          <Link
                            href={projectLinkForQRCode}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            Test the upload page here
                          </Link>
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          overflowX: 'auto',
                          pb: 1,
                          scrollSnapType: 'x mandatory',
                        }}
                      >
                        {latestEntries.map((clip, i) => (
                          <Card
                            key={i}
                            onClick={() => goToClipPage(clip)}
                            sx={{
                              minWidth: latestEntries.length > 2 ? 220 : 'calc(50% - 8px)',
                              maxWidth: 260,
                              flex: '0 0 auto',
                              cursor: 'pointer',
                              scrollSnapAlign: 'start',
                            }}
                          >
                            <CardMedia component="img" image={clip.imageLowResUrl} alt={clip.contributor} />
                            <CardContent>
                              <Typography variant="subtitle1">{clip.contributor || 'Unknown'}</Typography>
                              <Typography variant="subtitle1" color="textSecondary">
                                {moment(clip.createdAt).fromNow()}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={goToClipGalleryPage}
                        sx={{
                          color: '#6b50ff',
                          borderColor: '#6b50ff',
                          '&:hover': {
                            borderColor: '#6b50ff',
                            backgroundColor: 'transparent',
                          },
                          '&:active': {
                            borderColor: '#6b50ff',
                          },
                        }}
                      >
                        View all
                      </Button>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={styles.paper}>
                <Typography variant="h5" fontWeight="bold">
                  Locations
                </Typography>
                <Segment style={{ width: '100%', height: '300px' }}>
                  {Map ? (
                    <Map
                      style="mapbox://styles/mapbox/streets-v8"
                      fitBounds={bbox}
                      fitBoundsOptions={{
                        padding: 20,
                        maxZoom: 9,
                      }}
                      containerStyle={{
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <>
                        <ZoomControl />
                        {clipsWithLocation.map((clip, i) => (
                          <Layer
                            key={i}
                            type="symbol"
                            id={`marker-${clip.lat1}`}
                            layout={{ 'icon-image': 'marker-15' }}
                          >
                            <Feature coordinates={[clip.lon1!, clip.lat1!]} />
                          </Layer>
                        ))}
                      </>
                    </Map>
                  ) : (
                    <div
                      style={{
                        height: '100%',
                        width: '100%',
                        minHeight: 240,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f6f8fb',
                        borderRadius: 8,
                        color: '#6b7280',
                        textAlign: 'center',
                        padding: '1rem',
                      }}
                    >
                      Map preview unavailable in local development.
                    </div>
                  )}
                </Segment>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={styles.paper}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="h5" fontWeight="bold">
                    Insights
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={styles.statBox}>
                          <Typography variant="h6">Page Views</Typography>
                          <Typography variant="h4">{formatNumber(project.pageViews || 0)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={styles.statBox}>
                          <Typography variant="h6">Clips</Typography>
                          <Typography variant="h4">{formatNumber(project.clipCount || 0)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={styles.statBox}>
                          <Typography variant="h6">Compilations</Typography>
                          <Typography variant="h4">{formatNumber(project.episodeCount || 0)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={styles.statBox}>
                          <Typography variant="h6">Exports</Typography>
                          <Typography variant="h4">0</Typography>
                          {/* Uncomment and replace {exports} with new exports variable */}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <InviteCard project={project} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Project Settings
        <Header.Subheader>
          Configure your project details, choose your submission style, set limits and tailor every element of your
          video submission project.
        </Header.Subheader>
      </Header>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={styles.paper}>
                <Typography variant="h5" fontWeight="bold">
                  Project Overview
                </Typography>
                <TextField
                  label="Internal Name"
                  placeholder="Internal Name"
                  name="title"
                  fullWidth
                  value={projectTitle}
                  onChange={handleProjectTitleChange}
                  margin="normal"
                  error={validationError === 'title'}
                  helperText={validationError === 'title' && 'Please give a title to your project'}
                />
                <TextField
                  label="Public Name"
                  placeholder="Public Name"
                  name="name"
                  fullWidth
                  value={projectName}
                  onChange={handleProjectNameChange}
                  margin="normal"
                  error={validationError === 'name'}
                  helperText={validationError === 'name' && 'Please give a name to your project'}
                />
                <TextField
                  label="Reward"
                  placeholder="Enter Project Reward"
                  name="reward"
                  fullWidth
                  value={projectReward}
                  onChange={handleProjectRewardChange}
                  margin="normal"
                  error={validationError === 'reward'}
                  helperText={validationError === 'reward' && 'Please provide a reward for your project'}
                />
                <TextField
                  select
                  label="Locale"
                  defaultValue={projectLocale}
                  fullWidth
                  margin="normal"
                  onChange={handleProjectLocaleChange}
                >
                  {locales.map((option, i) => (
                    <MenuItem key={i} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="textSecondary">
                  Need help?{' '}
                  <ExternalLink url="https://vloggi.com/docs/how-to-change-contributor-languages-in-vloggi/">
                    Read how to change contributor languages.
                  </ExternalLink>
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
                  <FormControlLabel
                    label={isShared ? 'Active' : 'Inactive'}
                    control={
                      <Switch
                        checked={isShared}
                        onChange={handleProjectLiveChange}
                        name="shared"
                        sx={{
                          '& .MuiSwitch-switchBase': {
                            color: '#6b50ff !important',
                          },
                        }}
                      />
                    }
                    sx={{
                      color: isShared ? '#000000' : '#7f7f7f',
                    }}
                  />
                  <Box>
                    <Typography variant="body1" textAlign="right">
                      Expiry
                    </Typography>
                    <Typography textAlign="right">
                      {!project.deadline ? 'None set' : moment(project.deadline).format('MMMM D, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={styles.paper}>
                <Typography variant="h5" fontWeight="bold">
                  Survey Questions
                </Typography>
                <Typography variant="body1" my={1}>
                  You can ask up to eight questions. The contributor will see a prompt to answer each question with a
                  video response.&nbsp;
                  <ExternalLink url="https://vloggi.com/docs/building-a-video-questionnaire-in-vloggi-enhancing-engagement-and-insight/">
                    Read more.
                  </ExternalLink>
                </Typography>
                <TextField
                  select
                  label="Number of Prompts"
                  defaultValue={numberOfQuestions}
                  fullWidth
                  margin="normal"
                  onChange={(e) => handleDropdownChange(e, e.target)}
                >
                  {dropdownOptions.map((option, i) => (
                    <MenuItem key={i} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </TextField>
                {Array.from({ length: numberOfQuestions }).map((_, i) => (
                  <TextField
                    fullWidth
                    margin="normal"
                    label={`Prompt ${i + 1}`}
                    placeholder="Type your question here"
                    name={`question-${i + 1}`}
                    value={questions[i] || ''}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: MAX_QUESTIONS_CHARACTER_LENGTH }}
                  />
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BrandingCard
                project={project}
                handleChangePrimaryFont={handleChangePrimaryFont}
                handleChangePrimaryColor={handleChangePrimaryColor}
              />
            </Grid>

            <Grid item xs={12}>
              <FormatsCard project={project} handleChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <SettingsCard project={project} handleChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <LegalComplianceCard project={project} handleChange={handleChange} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

interface IProps extends RouteComponentProps<any> {
  project: IProject
  clips: IClip[]
  handleChange: (arg: any, data: any) => void
  handleChangePrimaryFont: (arg: { family: string }) => void
  handleChangePrimaryColor: (arg: { hex: string }) => void
  validationError: string | null
}

export default withRouter(ConfigureTabOverviewPage)
